import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { FeatureFlagsRes } from '../types/rest-api';

const ALL_OFF: FeatureFlagsRes = {
  multilanguage: false,
  applications_v2: false,
};

/**
 * Server-side switches from GET /v1/features. Everything is off until loaded
 * or when the call fails; `loaded` tells "off" apart from "not known yet".
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly http = inject(HttpClient);
  private readonly flags = signal<FeatureFlagsRes | null>(null);
  private pending: Observable<FeatureFlagsRes> | null = null;

  readonly loaded = computed(() => this.flags() !== null);
  readonly multilanguage = computed(() => this.flags()?.multilanguage ?? false);
  readonly applicationsV2 = computed(
    () => this.flags()?.applications_v2 ?? false
  );

  load(): Observable<FeatureFlagsRes> {
    this.pending = this.http
      .get<FeatureFlagsRes>(`${environment.apiBaseUrl}/v1/features`)
      .pipe(
        catchError(() => of(ALL_OFF)),
        map(flags => ({ ...ALL_OFF, ...flags })),
        tap(flags => this.flags.set(flags)),
        shareReplay(1)
      );
    return this.pending;
  }

  /** The switches once known, loading them if nobody has yet. */
  ready(): Observable<FeatureFlagsRes> {
    const flags = this.flags();
    if (flags) return of(flags);
    return this.pending ?? this.load();
  }
}
